'use client';

import { useMemo, useState } from 'react';

import {
  useCreateTaskAssigneeMutation,
  useDeleteTaskAssigneeMutation,
  useUpdateTaskAssigneeMutation,
} from '@/features/tasks/api/tasksApi';
import { AssigneeSelect } from '@/features/workspaces/components';
import {
  Badge,
  InlineButton,
  OptionsMenu,
  PlusIcon,
  ProgressMeter,
  SmartInput,
} from '@web/ui';
import {
  backendErrorsToFieldErrors,
  getErrorMessage,
  notify,
} from '@web/utils';

import styles from './JobTasksComponents.module.scss';

import type {
  Task,
  TaskAssignee,
  UpdateTaskPayload,
} from '@/features/tasks/types';
import type { CreateTaskAssigneePayload } from '@/features/tasks/types';

type DeliverableTaskSubRowFormProps = {
  task: Task;
  readOnly: boolean;
  availableServiceTypes?: Array<{ id: string; label: string }>;
  onAllocationsChanged?: () => void;
  onUpdateTask: (args: {
    taskId: string;
    payload: UpdateTaskPayload;
  }) => Promise<unknown>;
};

type FormErrors = Partial<
  Record<
    | 'description'
    | 'assignee_id'
    | 'service_type_id'
    | 'level_of_effort'
    | 'form',
    string
  >
>;

type AllocationFormState = {
  rowId: string;
  taskAssigneeId: string;
  userId: string;
  assigneeName: string;
  assigneeAvatar?: string;
  serviceTypeId: string;
  serviceTypeLabel: string;
  levelOfEffort: number | null;
  trackedHours: number;
  trackedPercentage: number | null;
};

function getAssigneeName(assignee: TaskAssignee): string {
  const user = assignee.user;
  if (!user) {
    return '';
  }

  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return fullName || user.email || '';
}

function getAssigneeAvatar(assignee: TaskAssignee): string | undefined {
  const user = assignee.user as
    | (TaskAssignee['user'] & {
        avatar_url?: string | null;
        avatar?: string | null;
      })
    | null;

  return user?.avatar_url ?? user?.avatar ?? undefined;
}

function toAllocationFormState(
  assignee: TaskAssignee,
  index: number,
): AllocationFormState {
  return {
    rowId: assignee.id || `allocation-${index}`,
    taskAssigneeId: assignee.id || '',
    userId: assignee.user?.id ?? '',
    assigneeName: getAssigneeName(assignee),
    assigneeAvatar: getAssigneeAvatar(assignee),
    serviceTypeId: assignee.service_type_id ?? assignee.service_type?.id ?? '',
    serviceTypeLabel: assignee.service_type?.label ?? '',
    levelOfEffort: assignee.level_of_effort ?? null,
    trackedHours: assignee.tracked_hours ?? 0,
    trackedPercentage: assignee.actual_time_tracked_percentage ?? null,
  };
}

function createEmptyAllocationFormState(): AllocationFormState {
  return {
    rowId: `allocation-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    taskAssigneeId: '',
    userId: '',
    assigneeName: '',
    assigneeAvatar: undefined,
    serviceTypeId: '',
    serviceTypeLabel: '',
    levelOfEffort: null,
    trackedHours: 0,
    trackedPercentage: null,
  };
}

function mapBackendPath(path: string): string | null {
  if (path === 'description.0' || path === 'description') return 'description';
  if (path === 'task_assignee_payloads.0.user_id') return 'assignee_id';
  if (path === 'task_assignee_payloads.0.service_type_id')
    return 'service_type_id';
  if (path === 'task_assignee_payloads.0.level_of_effort')
    return 'level_of_effort';
  if (path === 'user_id.0' || path === 'user_id') return 'assignee_id';
  if (path === 'service_type_id.0' || path === 'service_type_id')
    return 'service_type_id';
  if (path === 'level_of_effort.0' || path === 'level_of_effort')
    return 'level_of_effort';
  if (path === 'non_field_errors.0') return 'form';

  return path;
}

export function DeliverableTaskSubRowForm({
  task,
  readOnly,
  availableServiceTypes = [],
  onAllocationsChanged,
  onUpdateTask,
}: DeliverableTaskSubRowFormProps) {
  const initialAllocations =
    task.assignees.length > 0
      ? task.assignees.map(toAllocationFormState)
      : [createEmptyAllocationFormState()];

  const [createTaskAssignee] = useCreateTaskAssigneeMutation();
  const [updateTaskAssignee] = useUpdateTaskAssigneeMutation();
  const [deleteTaskAssignee] = useDeleteTaskAssigneeMutation();

  const [description, setDescription] = useState(task.description ?? '');
  const [allocations, setAllocations] =
    useState<AllocationFormState[]>(initialAllocations);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const restrictedServiceTypeOptions = useMemo(() => {
    const unique = new Map<string, string>();
    for (const serviceType of availableServiceTypes) {
      if (!serviceType.id || !serviceType.label) {
        continue;
      }
      if (!unique.has(serviceType.id)) {
        unique.set(serviceType.id, serviceType.label);
      }
    }
    return Array.from(unique.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [availableServiceTypes]);
  const serviceTypeSelectOptions = useMemo(
    () =>
      restrictedServiceTypeOptions.length > 0
        ? restrictedServiceTypeOptions
        : [{ value: '', label: 'No service types on this deliverable' }],
    [restrictedServiceTypeOptions],
  );

  const totalLoe = allocations.reduce(
    (sum, allocation) => sum + (allocation.levelOfEffort ?? 0),
    0,
  );

  const clearError = (field: keyof FormErrors) => {
    setErrors((current) => {
      if (!current[field]) {
        return current;
      }

      return {
        ...current,
        [field]: undefined,
      };
    });
  };

  const updateAllocation = (
    rowId: string,
    updater: (current: AllocationFormState) => AllocationFormState,
  ) => {
    setAllocations((current) =>
      current.map((allocation) =>
        allocation.rowId === rowId ? updater(allocation) : allocation,
      ),
    );
  };

  const getAllocation = (rowId: string) =>
    allocations.find((allocation) => allocation.rowId === rowId);

  const saveTaskChanges = async (payload: UpdateTaskPayload) => {
    if (readOnly) {
      return;
    }

    try {
      setIsSaving(true);
      setErrors({});
      await onUpdateTask({ taskId: task.id, payload });
    } catch (error) {
      const backendErrors = (error as any)?.data?.errors;
      const mappedErrors = backendErrorsToFieldErrors(backendErrors, {
        mapPath: mapBackendPath,
      });
      if (Object.keys(mappedErrors).length > 0) {
        setErrors(mappedErrors as FormErrors);
      }
      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setIsSaving(false);
    }
  };

  const saveAssigneeChanges = async ({
    rowId,
    nextAssigneeId,
    nextServiceTypeId,
    nextLevelOfEffort,
  }: {
    rowId: string;
    nextAssigneeId: string;
    nextServiceTypeId: string;
    nextLevelOfEffort: number | null;
  }) => {
    if (readOnly) {
      return;
    }

    if (!nextAssigneeId) {
      setErrors((current) => ({
        ...current,
        assignee_id: 'Assignee is required.',
      }));
      return;
    }

    const currentAllocation = getAllocation(rowId);
    if (!currentAllocation) {
      return;
    }

    try {
      setIsSaving(true);
      setErrors({});
      if (currentAllocation.taskAssigneeId) {
        await updateTaskAssignee({
          taskAssigneeId: currentAllocation.taskAssigneeId,
          payload: {
            user_id: nextAssigneeId,
            service_type_id: nextServiceTypeId || null,
            level_of_effort: nextLevelOfEffort ?? 0,
          },
        }).unwrap();
        return;
      }

      const payload: CreateTaskAssigneePayload = {
        user_id: nextAssigneeId,
        service_type_id: nextServiceTypeId || null,
        level_of_effort: nextLevelOfEffort ?? 0,
      };
      const createdAssignment = await createTaskAssignee({
        taskId: task.id,
        payload,
      }).unwrap();

      const createdAssigneeId = createdAssignment?.data?.id ?? '';
      if (createdAssigneeId) {
        updateAllocation(rowId, (allocation) => ({
          ...allocation,
          taskAssigneeId: createdAssigneeId,
          rowId: createdAssigneeId,
          trackedHours: createdAssignment?.data?.tracked_hours ?? 0,
          trackedPercentage:
            createdAssignment?.data?.actual_time_tracked_percentage ?? null,
        }));
      }
    } catch (error) {
      const backendErrors = (error as any)?.data?.errors;
      const mappedErrors = backendErrorsToFieldErrors(backendErrors, {
        mapPath: mapBackendPath,
      });
      if (Object.keys(mappedErrors).length > 0) {
        setErrors(mappedErrors as FormErrors);
      }
      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setIsSaving(false);
    }
  };

  const getAllocationProgress = (allocation: AllocationFormState): number => {
    if (allocation.levelOfEffort === null || allocation.levelOfEffort <= 0) {
      return 0;
    }

    if (typeof allocation.trackedPercentage === 'number') {
      return allocation.trackedPercentage;
    }

    return (allocation.trackedHours / allocation.levelOfEffort) * 100;
  };

  return (
    <div className={styles.deliverableTaskSubRowForm}>
      <SmartInput
        type="textarea"
        name={`deliverable-task-description-${task.id}`}
        label="Description"
        value={description}
        readOnly={readOnly}
        error={errors.description}
        onChange={(nextValue) => {
          setDescription(nextValue);
          clearError('description');
        }}
        onBlur={() => {
          void saveTaskChanges({
            description: description.trim() || null,
          });
        }}
      />

      <div className={styles.inlineAllocationsHeader}>
        <p className={styles.inlineAllocationsTitle}>Allocations</p>
        <Badge label={`Total LOE:${totalLoe}Hrs`} tone="info" size="lg" />
      </div>

      <div className={styles.allocationsColumnsHeader}>
        <span>Assignee</span>
        <span>Service type</span>
        <span>LOE</span>
        <span>Metrics</span>
      </div>

      <div className={styles.inlineAllocationsRows}>
        {allocations.map((allocation) => {
          const trackedHours = allocation.trackedHours ?? 0;
          const plannedHours = allocation.levelOfEffort ?? 0;
          const progress = getAllocationProgress(allocation);

          return (
            <div key={allocation.rowId} className={styles.editAllocationRow}>
              <div className={styles.editAllocationAssigneeCell}>
                <AssigneeSelect
                  value={allocation.userId}
                  selectedLabel={allocation.assigneeName || undefined}
                  selectedAvatarSrc={allocation.assigneeAvatar}
                  error={errors.assignee_id}
                  disabled={readOnly}
                  showPreview={false}
                  label=""
                  name={`deliverable-task-assignee-${task.id}-${allocation.rowId}`}
                  onChange={(nextValue) => {
                    updateAllocation(allocation.rowId, (current) => ({
                      ...current,
                      userId: nextValue,
                      assigneeName: '',
                      assigneeAvatar: undefined,
                    }));
                    clearError('assignee_id');
                    clearError('service_type_id');
                    clearError('level_of_effort');
                    void saveAssigneeChanges({
                      rowId: allocation.rowId,
                      nextAssigneeId: nextValue,
                      nextServiceTypeId: allocation.serviceTypeId,
                      nextLevelOfEffort: allocation.levelOfEffort,
                    });
                  }}
                />
              </div>

              <SmartInput
                type="select"
                name={`deliverable-task-service-type-${task.id}-${allocation.rowId}`}
                label=""
                value={allocation.serviceTypeId}
                options={serviceTypeSelectOptions}
                error={errors.service_type_id}
                readOnly={readOnly || restrictedServiceTypeOptions.length === 0}
                onChange={(nextValue) => {
                  const nextServiceTypeId = String(nextValue || '');
                  updateAllocation(allocation.rowId, (current) => ({
                    ...current,
                    serviceTypeId: nextServiceTypeId,
                  }));
                  clearError('service_type_id');
                  void saveAssigneeChanges({
                    rowId: allocation.rowId,
                    nextAssigneeId: allocation.userId,
                    nextServiceTypeId,
                    nextLevelOfEffort: allocation.levelOfEffort,
                  });
                }}
              />

              {readOnly ? (
                <div>
                  <p className="readonlyLabel">LOE (hrs)</p>
                  <p className="permission-readonly-value">
                    {allocation.levelOfEffort ?? 0}
                  </p>
                </div>
              ) : (
                <SmartInput
                  type="number"
                  name={`deliverable-task-level-of-effort-${task.id}-${allocation.rowId}`}
                  label=""
                  value={allocation.levelOfEffort}
                  min={0}
                  step={0.1}
                  error={errors.level_of_effort}
                  onChange={(nextValue) => {
                    updateAllocation(allocation.rowId, (current) => ({
                      ...current,
                      levelOfEffort: nextValue,
                    }));
                    clearError('level_of_effort');
                  }}
                  onBlur={() => {
                    void saveAssigneeChanges({
                      rowId: allocation.rowId,
                      nextAssigneeId: allocation.userId,
                      nextServiceTypeId: allocation.serviceTypeId,
                      nextLevelOfEffort: allocation.levelOfEffort,
                    });
                  }}
                />
              )}

              <div className={styles.editAllocationMetricsCell}>
                <ProgressMeter percentage={progress} size="sm" />
                <span>{`LOE ${trackedHours} / ${plannedHours}h`}</span>
              </div>

              <div className={styles.editAllocationActionsCell}>
                {readOnly ? null : (
                  <OptionsMenu
                    ariaLabel="Allocation actions"
                    actions={[
                      {
                        key: 'delete',
                        label: 'Delete',
                        tone: 'danger',
                        onSelect: async () => {
                          if (allocation.taskAssigneeId) {
                            try {
                              await deleteTaskAssignee({
                                taskAssigneeId: allocation.taskAssigneeId,
                              }).unwrap();
                              setAllocations((current) => {
                                const next = current.filter(
                                  (row) => row.rowId !== allocation.rowId,
                                );
                                return next.length > 0
                                  ? next
                                  : [createEmptyAllocationFormState()];
                              });
                              onAllocationsChanged?.();
                              notify({
                                type: 'success',
                                message: 'Allocation removed.',
                              });
                            } catch (error) {
                              notify({
                                type: 'error',
                                message: getErrorMessage(error),
                              });
                            }
                            return;
                          }

                          setAllocations((current) => {
                            const next = current.filter(
                              (row) => row.rowId !== allocation.rowId,
                            );
                            return next.length > 0
                              ? next
                              : [createEmptyAllocationFormState()];
                          });
                        },
                      },
                    ]}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!readOnly ? (
        <InlineButton
          ariaLabel="Add assignee allocation"
          onClick={() =>
            setAllocations((current) => [
              ...current,
              createEmptyAllocationFormState(),
            ])
          }
        >
          <>
            <PlusIcon />
            &nbsp;Assignee
          </>
        </InlineButton>
      ) : null}

      <p className={styles.deliverableTaskSubRowStatus}>
        {errors.form ? errors.form : isSaving ? 'Saving changes...' : ''}
      </p>
    </div>
  );
}
